"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { SlidersHorizontal } from "lucide-react";
import { usePathname, useRouter } from "@/i18n/navigation";
import { Button } from "@/shared/ui/buttons/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/shared/ui/modals/sheet";
import { useAuthStore } from "@/shared/model/auth-store";
import { useUserProfile } from "@/hooks/useAuth";
import {
  useCreateLfgLftApplication,
  useLfgLftApplicationsIncoming,
  useLfgLftApplicationsOutgoing,
  useLfgLftPost,
  useLfgLftPosts,
  useUpdateLfgLftPost,
} from "@/hooks/useLfgLft";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import { countActiveFilters, toListParams, type BoardFilters } from "@/features/lfg-board/model/board-filters";
import { useBoardFilters } from "@/features/lfg-board/model/use-board-filters";
import { formatUtcOffset, useViewerTzOffset } from "@/features/lfg-board/model/use-viewer-tz-offset";
import { useManagedTeams } from "@/features/lfg-board/api/use-managed-teams";
import { BoardFiltersPanel } from "@/features/lfg-board/ui/board-filters-panel";
import { BoardToolbar } from "@/features/lfg-board/ui/board-toolbar";
import { BoardTeamCta } from "@/features/lfg-board/ui/board-team-cta";
import { PostFormDialog, type PostFormMode } from "@/features/lfg-board/ui/post-form-dialog";
import { ApplicationsDialog } from "@/features/lfg-board/ui/applications-dialog";
import { TeamBuilderDialog } from "@/features/lfg-board/ui/team-builder-dialog";
import { RespondDialog, type RespondTarget } from "@/features/lfg-board/ui/respond-dialog";
import { ClosePostSuggestion } from "@/features/lfg-board/ui/close-post-suggestion";
import { countNewApplications } from "@/features/lfg-board/model/applications";
import { BoardPostList } from "./board-post-list";

const PAGE_SIZE = 20;
const MSK_OFFSET = -180;

export function LfgBoard() {
  const t = useTranslations("LfgBoard");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [filters, setFilters] = useBoardFilters();
  const tzOffset = useViewerTzOffset();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: profile } = useUserProfile();
  const managedTeams = useManagedTeams(profile?.id ?? undefined);
  const incoming = useLfgLftApplicationsIncoming(isAuthenticated);
  const outgoing = useLfgLftApplicationsOutgoing(isAuthenticated);
  // Форма создания/редактирования: одна на страницу, режим задаёт, что в ней открыто.
  const [formMode, setFormMode] = useState<PostFormMode | null>(null);
  const [appsOpen, setAppsOpen] = useState(false);
  const [closeSuggestId, setCloseSuggestId] = useState<string | null>(null);
  // «Собрать состав» открывается с карточки своей команды: id команды, чей состав собираем.
  const [builderTeamId, setBuilderTeamId] = useState<string | null>(null);
  const [respondTarget, setRespondTarget] = useState<RespondTarget | null>(null);
  const updatePost = useUpdateLfgLftPost();
  const createApp = useCreateLfgLftApplication();

  // Часы фильтра — в поясе зрителя; пока пояс неизвестен (гидрация), запрос не уходит.
  const posts = useLfgLftPosts(tzOffset === null ? null : toListParams(filters, tzOffset, PAGE_SIZE));
  const total = posts.data?.totalCount;
  const totalPages = Math.max(1, Math.ceil((total ?? 0) / PAGE_SIZE));
  const newApplications = countNewApplications(incoming.data);
  const managed = managedTeams.data?.managed ?? [];
  const managedTeamIds = new Set(managed.map((team) => team.id));
  // «Отклик отправлен» — пока заявка ждёт ответа или уже мэтч; отклонённую можно подать снова.
  const appliedPostIds = new Set(
    (outgoing.data ?? []).filter((a) => a.status === "Pending" || a.status === "Accepted").map((a) => a.postId),
  );
  const tzLabel = tzOffset === null ? "" : tzOffset === MSK_OFFSET ? t("tzMsk") : formatUtcOffset(tzOffset);

  /**
   * Deep-link из уведомления: `/lfg?tab=applications` открывает диалог заявок один раз
   * (KAN-921); параметр сразу вычищается, чтобы «назад» не открывал его снова.
   */
  useEffect(() => {
    const wantsApps = searchParams.get("tab") === "applications";
    const closePost = searchParams.get("closePost");
    if (!wantsApps && !closePost) return;
    if (wantsApps) setAppsOpen(true);
    // Ссылка из уведомления «закрыть объявление?» (KAN-1378): показываем вопрос, сами не закрываем.
    if (closePost) setCloseSuggestId(closePost);
    const url = new URL(window.location.href);
    url.searchParams.delete("tab");
    url.searchParams.delete("closePost");
    window.history.replaceState({}, "", url.toString());
  }, [searchParams]);

  /** Гостю действие открывает вход с возвратом на доску, а не молчаливую ошибку 401. */
  const requireAuth = (action: () => void) => {
    if (isAuthenticated) return action();
    router.push(`/login?redirect=${encodeURIComponent(`${pathname}?${searchParams.toString()}`)}`);
  };

  const isAuthor = (post: LfgLftPostResponse) => {
    const id = post.author?.id;
    if (!profile?.id || !id) return false;
    return post.author?.type === "User" ? id === profile.id : managedTeamIds.has(id);
  };
  /** Объявление команды, в которой я состою, но не управляю: откликаться на своих нельзя. */
  const isOwnTeamPost = (post: LfgLftPostResponse) =>
    post.author?.type === "Team" && !!post.author.id && !!managedTeams.data?.memberIds.has(post.author.id);

  const openCreate = () => setFormMode({ kind: "create", defaultType: segmentToPostType(filters) });
  const setPage = (page: number) => setFilters({ ...filters, page });
  const filtersPanel = (
    <BoardFiltersPanel filters={filters} onChange={setFilters} tzLabel={tzLabel} />
  );

  const focusId = searchParams.get("post");
  const focused = useLfgLftPost(focusId);
  const dismissFocus = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("post");
    router.replace(`${pathname}${url.search}`);
  };

  const renderList = (source: { isLoading: boolean; isError: boolean; onRetry: () => void; items: LfgLftPostResponse[] }) => (
  <BoardPostList
    tzOffset={tzOffset}
    {...source}
    isAuthor={isAuthor}
    appliedPostIds={appliedPostIds}
    inviteTeams={managed}
    isOwnTeamPost={isOwnTeamPost}
          onBuildTeam={setBuilderTeamId}
    actionsDisabled={createApp.isPending}
    onEdit={(post) => setFormMode({ kind: "edit", post })}
    onClose={(id) => updatePost.mutate({ id, body: { status: "Closed" } })}
    onApply={(postId, targetRole) =>
      requireAuth(() =>
        setRespondTarget({
          postId,
          role: targetRole,
          toName: posts.data?.items.find((x) => x.id === postId)?.author?.displayName ?? "",
        }),
      )
    }
    // Роль приглашения бэкенд берёт из объявления игрока — targetRole здесь не читается.
    onInvite={(postId, sourceTeamId, message) =>
      requireAuth(() => createApp.mutate({ postId, body: { targetRole: "", sourceTeamId, message: message.trim() || null } }))
    }
  />
  );

  return (
    <div className="mx-auto flex w-full max-w-[1280px] items-start gap-6 px-4 py-6 sm:px-6">
      {/* Свой скролл: липкая колонка выше экрана иначе прячет нижние группы фильтров. */}
      <aside className="sticky top-[var(--gl-sticky-top)] hidden max-h-[calc(100dvh-var(--gl-sticky-top)-1rem)] w-[clamp(200px,24%,268px)] flex-none overflow-y-auto overscroll-contain rounded-[22px] border border-border bg-muted/20 p-4 pb-7 [scrollbar-width:none] lg:block [&::-webkit-scrollbar]:hidden">
        <BoardActions
          newApplications={newApplications}
          onCreate={() => requireAuth(openCreate)}
          onApplications={() => requireAuth(() => setAppsOpen(true))}
        />
        {filtersPanel}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-col gap-2 lg:hidden">
          <BoardActions
            newApplications={newApplications}
            onCreate={() => requireAuth(openCreate)}
            onApplications={() => requireAuth(() => setAppsOpen(true))}
          />
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="justify-start gap-2">
                <SlidersHorizontal className="size-4" />
                {t("filtersButton")}
                {countActiveFilters(filters) > 0 && <span className="text-highlight">({countActiveFilters(filters)})</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto px-4 pb-6">
              <SheetHeader className="px-0">
                <SheetTitle>{t("filtersTitle")}</SheetTitle>
              </SheetHeader>
              {filtersPanel}
            </SheetContent>
          </Sheet>
        </div>

        {isAuthenticated && managedTeams.isSuccess && !managedTeams.data.hasMainTeam && <BoardTeamCta />}

        {focusId && (
          <section className="flex flex-col gap-2 rounded-2xl border border-highlight-border bg-highlight-soft/30 p-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-highlight">{t("focus.title")}</h2>
              <Button variant="ghost" size="sm" onClick={dismissFocus}>
                {t("focus.dismiss")}
              </Button>
            </div>
            {focused.isSuccess && (!focused.data || focused.data.status !== "Active") ? (
              <p className="text-sm text-muted-foreground">{t("focus.closed")}</p>
            ) : (
              renderList({ isLoading: focused.isLoading, isError: focused.isError, onRetry: () => void focused.refetch(), items: focused.data ? [focused.data] : [] })
            )}
          </section>
        )}

        <BoardToolbar filters={filters} onChange={setFilters} total={posts.isSuccess ? total : undefined} />

        {renderList({ isLoading: posts.isLoading, isError: posts.isError, onRetry: () => void posts.refetch(), items: posts.data?.items ?? [] })}

        {totalPages > 1 && (
          <nav className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={filters.page <= 1} onClick={() => setPage(filters.page - 1)}>
              {t("prev")}
            </Button>
            <span className="px-2 text-sm text-muted-foreground">{t("pageOf", { page: filters.page, total: totalPages })}</span>
            <Button variant="outline" size="sm" disabled={filters.page >= totalPages} onClick={() => setPage(filters.page + 1)}>
              {t("next")}
            </Button>
          </nav>
        )}
      </div>

      <PostFormDialog
        open={formMode !== null}
        onOpenChange={(open) => !open && setFormMode(null)}
        mode={formMode ?? { kind: "create", defaultType: segmentToPostType(filters) }}
        teams={managed}
        tzOffset={tzOffset}
        tzLabel={tzLabel}
        profileLanguages={profile?.languages ?? undefined}
      />
      {builderTeamId && (
        <TeamBuilderDialog open onOpenChange={(open) => !open && setBuilderTeamId(null)} teamId={builderTeamId} />
      )}
      <ApplicationsDialog open={appsOpen} onOpenChange={setAppsOpen} />
      <RespondDialog
        target={respondTarget}
        pending={createApp.isPending}
        onCancel={() => setRespondTarget(null)}
        onSend={(target, message) => {
          createApp.mutate(
            { postId: target.postId, body: { targetRole: target.role, message: message.trim() || null } },
            { onSuccess: () => setRespondTarget(null) },
          );
        }}
      />
      <ClosePostSuggestion
        postId={closeSuggestId}
        onDismiss={() => setCloseSuggestId(null)}
        onConfirm={(id) => {
          updatePost.mutate({ id, body: { status: "Closed" } });
          setCloseSuggestId(null);
        }}
      />
    </div>
  );
}

/** Модалка создания по умолчанию предлагает тип, который человек сейчас смотрит. */
function segmentToPostType(f: BoardFilters): "Lfg" | "Lft" {
  return f.segment === "teams" ? "Lft" : "Lfg";
}

function BoardActions(p: { newApplications: number; onCreate: () => void; onApplications: () => void }) {
  const t = useTranslations("LfgBoard");
  return (
    <div className="mb-3.5 flex flex-col gap-2">
      <Button
        variant="outline"
        className="h-[38px] border-highlight-border bg-highlight-soft font-bold text-highlight hover:bg-highlight-soft/80 hover:text-highlight"
        onClick={p.onCreate}
      >
        {t("createPost")}
      </Button>
      <Button variant="outline" className="h-[38px] gap-2 font-semibold" onClick={p.onApplications}>
        {t("applications")}
        {p.newApplications > 0 && (
          <span
            aria-label={t("applicationsCount", { count: p.newApplications })}
            className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-highlight-soft px-1.5 text-[11px] font-bold text-highlight"
          >
            {p.newApplications}
          </span>
        )}
      </Button>
    </div>
  );
}
